import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";
import { trackRouteView } from "@/lib/heycatch";
import { applySeo } from "@/lib/seo";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { Spinner } from "@/components/common/Loading";

import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import BrowseSponsors from "@/pages/BrowseSponsors";
import SponsorProfile from "@/pages/SponsorProfile";
import Messages from "@/pages/Messages";
import Favorites from "@/pages/Favorites";
import BecomeSponsor from "@/pages/BecomeSponsor";
import Pricing from "@/pages/Pricing";
import Dashboard from "@/pages/Dashboard";
import SponsorDashboard from "@/pages/SponsorDashboard";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import Resources from "@/pages/Resources";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import VsAiSponsor from "@/pages/VsAiSponsor";
import Updates from "@/pages/Updates";
import NotFound from "@/pages/NotFound";
import CampaignRedirect from "@/pages/CampaignRedirect";
import Admin from "@/pages/Admin";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner label="Loading your account" />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

/**
 * A one-segment path is either a reserved HeyCatch campaign short link
 * (single character: /a-/z, /0-/9) or an unknown page. Anything else that is a
 * real route is matched earlier by its own <Route>, so reaching here means the
 * path is not part of the app.
 */
function ShortPathRoute() {
  const { shortCode } = useParams();
  const isReserved = /^[a-z0-9]$/i.test(shortCode || "");
  return isReserved ? <CampaignRedirect /> : <NotFound />;
}

function RouteMeta() {
  const location = useLocation();
  const { user, loading } = useAuth();

  // Land at the top of every new page so content never starts hidden
  // underneath the sticky header.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  useEffect(() => {
    applySeo(location.pathname);
    trackPageView(location.pathname, user?.id || null);
  }, [location.pathname, user?.id]);

  // Named HeyCatch funnel view for the handful of routes that matter to the
  // business funnel. Keyed on location.key so it fires once per real
  // navigation (including back/forward) and never on a re-render. We wait for
  // the auth check to settle so `authenticated` is accurate rather than
  // reporting every first paint as anonymous.
  useEffect(() => {
    if (loading) return;
    trackRouteView(location.pathname, location.key, {
      authenticated: Boolean(user?.id),
    });
  }, [location.pathname, location.key, loading, user?.id]);
  return null;
}

function Shell() {
  return (
    <AppShell>
      <RouteMeta />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/sponsors" element={<BrowseSponsors />} />
        <Route path="/sponsors/:userId" element={<SponsorProfile />} />
        <Route path="/pricing" element={<Pricing />} />
        {/* HeyCatch reports real traffic landing on /plans (external posts and
            older links point there). There has never been a /plans page in this
            app - /pricing is the canonical route - so redirect rather than
            duplicate the page: one URL, one canonical, and the single
            `pricing_viewed` event still fires exactly once because `replace`
            leaves one history entry behind. */}
        <Route path="/plans" element={<Navigate to="/pricing" replace />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/vs/ai-sponsor" element={<VsAiSponsor />} />
            <Route path="/updates" element={<Updates />} />
        <Route path="/become-a-sponsor" element={<BecomeSponsor />} />
        <Route
          path="/messages"
          element={
            <Protected>
              <Messages />
            </Protected>
          }
        />
        <Route
          path="/favorites"
          element={
            <Protected>
              <Favorites />
            </Protected>
          }
        />
        <Route
          path="/dashboard"
          element={
            <Protected>
              <Dashboard />
            </Protected>
          }
        />
        <Route
          path="/sponsor-dashboard"
          element={
            <Protected>
              <SponsorDashboard />
            </Protected>
          }
        />
        <Route
          path="/notifications"
          element={
            <Protected>
              <Notifications />
            </Protected>
          }
        />
        <Route
          path="/settings"
          element={
            <Protected>
              <Settings />
            </Protected>
          }
        />
        <Route
          path="/subscription/success"
          element={
            <Protected>
              <SubscriptionSuccess />
            </Protected>
          }
        />
        <Route
          path="/admin"
          element={
            <Protected adminOnly>
              <Admin />
            </Protected>
          }
        />
        {/* HeyCatch reserves every single-character path (/a-/z, /0-/9) for
            campaign attribution, so those must keep reaching the app and must
            NOT fall through to the 404 route. Matched explicitly, ahead of the
            catch-all, rather than with a broad pattern. */}
        <Route path="/:shortCode" element={<ShortPathRoute />} />
        {/* Everything else is a genuine miss: keep the URL, say so, noindex it.
            A true HTTP 404 also needs the hosting rule in
            docs/http-and-headers.md - React alone cannot change the status. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Shell />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
